import { useCallback, useEffect, useState } from 'react';
import { DataService } from '../services/http-interceptor.service';

export type StageCounts = {
  borrador: number;
  vobo: number;
  firma: number;
  rechazado: number;
  listo: number;
};

export type DocumentMetrics = {
  etapas: StageCounts;
  /**
   * Documentos de la sucursal, sin contar borradores. Va aparte de `etapas`
   * porque los borradores son personales y sumarlos daba un total inflado.
   */
  totalDocumentos: number;
  kpis: {
    circulacion: number;
    esperandoVobo: number;
    esperandoFirma: number;
    firmadosMes: number;
  };
  serieMensual: Array<{ mes: string; vobo: number; firma: number; rechazado: number; listo: number }>;
  recientes: Array<{ id: string; name: string; stage: string; dateInit: string; cliente?: string }>;
  actividad: Array<{ id: string; documento: string; titulo: string; fecha: string }>;
};

/**
 * Métricas de Inicio (GET /documents/metrics).
 *
 * La consumen el menú del AdminLayout e Inicio a la vez, y cada vez que se
 * vuelve a entrar a Inicio. Para que navegar no dispare peticiones (ni el
 * loader de página completa) el hook trae caché de módulo:
 *
 *   · Dos consumidores montados a la vez comparten UNA sola petición en
 *     vuelo (deduplicación).
 *   · Volver a entrar dentro de la vigencia no pide nada: los datos se
 *     entregan al instante desde caché.
 *   · Caducada la vigencia, se muestra lo cacheado y se revalida en
 *     segundo plano; nadie ve un loader por un refresco.
 */
const CACHE_TTL = 60_000;

let cache: { data: DocumentMetrics; at: number } | null = null;
let inflight: Promise<DocumentMetrics | null> | null = null;

const fetchMetrics = (): Promise<DocumentMetrics | null> => {
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const response = await DataService.get('documents/metrics');
      const data = (response?.data ?? null) as DocumentMetrics | null;
      if (data) cache = { data, at: Date.now() };
      return data;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
};

/** Invalida la caché (p. ej. tras crear/firmar un documento) y avisa a los
 *  consumidores montados para que revaliden al instante. */
const subscriptores = new Set<() => void>();

export const invalidateDocumentMetrics = () => {
  cache = null;
  subscriptores.forEach((avisar) => avisar());
};

export const useDocumentMetrics = () => {
  // Estado inicial desde caché: quien ya tiene datos no pasa por el loader.
  const [data, setData] = useState<DocumentMetrics | null>(cache?.data ?? null);
  const [isLoading, setIsLoading] = useState(!cache);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!cache) setIsLoading(true);
    try {
      const fresh = await fetchMetrics();
      setData(fresh);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Error al cargar métricas');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const vigente = cache && Date.now() - cache.at < CACHE_TTL;
    // Vigente: cero peticiones. Vencida o inexistente: revalidar (mostrando
    // lo cacheado, si hay, mientras llega lo nuevo).
    if (!vigente) refetch();
  }, [refetch]);

  // Si un flujo invalida (documento creado/firmado), este consumidor montado
  // revalida al momento: el contador del menú se refresca en vivo.
  useEffect(() => {
    const avisar = () => refetch();
    subscriptores.add(avisar);
    return () => {
      subscriptores.delete(avisar);
    };
  }, [refetch]);

  return { data, isLoading, error, refetch };
};

export default useDocumentMetrics;
